import { styled } from "@linaria/react";
import React from "react";
import { usePut } from "../../../../api/useCall";
import useSortedData from "../../../../lib/hooks/useSortedData";
import t from "../../../../i18n";
import { indexBy } from "diagnostics-lib/iterable";
import Button from "@ui/Button";
import ModalForm from "@ui/Modal/Form";
import Select from "@ui/Select";
import Table from "@ui/Table";
import type { ColumnMetaData } from "@ui/Table/ColumnMetaData";
import { useCanUserDoOnProject } from "../../../canUserDo";
import ButtonIconV2 from "@ui/ButtonIconV2";
import { AddCircleLine, DeleteBinLine } from "@ui/IconV2/react";
import { css } from "@linaria/core";
import type { Quantity } from "../../../../api/useQuantities";
import { MAX_PROCESS_INDICATOR_COUNT } from "diagnostics-domain/quantity/processIndicator";
const QoiOrder = styled.div`
  text-align: center;
  width: 100%;
`;

const StyledButton = styled(Button)`
  align-self: flex-start;
`;

const CellSelect = css`
  width: 100%;
`;

const columns = ["name", "qoiOrder", "deleteIcon"] as const;
type Column = (typeof columns)[number];

interface Qoi {
  id: string;
  processIndicatorOrder: number;
  name: string;
}

const computeRowKey = (qoi: Qoi) => `qoi-${qoi.processIndicatorOrder}-${qoi.id}`;

interface P {
  closeModal: () => void;
  processIndicators: { id: string; processIndicatorOrder: number; name: string }[];
  quantities: Quantity[];
  projectId: string;
}

const ManageProcessIndicatorModalForm = ({
  quantities,
  projectId,
  closeModal,
  processIndicators,
}: P) => {
  const canUserEdit = useCanUserDoOnProject("PROJECT_SETUP", projectId);
  const updateProcessIndicators = usePut("/projects/:projectId(uuid)/quantities/processIndicators");
  const sortedProcessIndicatorOrder = useSortedData(
    processIndicators,
    qoi => qoi.processIndicatorOrder,
    "asc",
  );
  const [processIndicatorsData, setProcessIndicators] = React.useState(sortedProcessIndicatorOrder);
  const quantityOptions = React.useMemo(
    () =>
      quantities
        .filter(quantity => !processIndicatorsData.some(qoi => qoi.name === quantity.name))
        .map(quantity => ({ value: quantity.id, label: quantity.name }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [processIndicatorsData, quantities],
  );
  const quantityById = indexBy(quantityOptions, "value");

  const metadata = React.useMemo(() => {
    const _: { [key in Column]: ColumnMetaData<Qoi> } = {
      name: {
        label: t("project.settings.qoi.table.column.quantity"),
        render: qoi =>
          qoi.name === "" ? (
            <Select
              filtered
              className={CellSelect}
              onChange={e => {
                const quantityId = e.target.value;
                const quantity = quantityById.get(quantityId);
                if (!quantity) throw new Error(`Quantity not found ${quantityId}`);

                setProcessIndicators(previousQois =>
                  previousQois.map(q =>
                    q.processIndicatorOrder === qoi.processIndicatorOrder
                      ? { ...q, id: quantityId, name: quantity.label }
                      : q,
                  ),
                );
              }}
              value=""
              options={quantityOptions}
              prompt=""
            />
          ) : (
            <>{qoi.name}</>
          ),
      },
      qoiOrder: {
        label: t("project.settings.qoi.table.column.order"),
        render: qoi => <QoiOrder>{processIndicatorsData.indexOf(qoi) + 1}</QoiOrder>,
        width: "130px",
      },
      deleteIcon: {
        label: null,
        width: "40px",
        render: qoi =>
          canUserEdit ? (
            <ButtonIconV2
              onClick={() => {
                setProcessIndicators(previousQois =>
                  previousQois.filter(q => q.processIndicatorOrder !== qoi.processIndicatorOrder),
                );
              }}
            >
              <DeleteBinLine />
            </ButtonIconV2>
          ) : (
            <></>
          ),
      },
    };

    return _;
  }, [canUserEdit, processIndicatorsData, quantityById, quantityOptions]);

  const maxProcessIndicatorsOrder = React.useMemo(
    () => processIndicatorsData.reduce((acc, qoi) => Math.max(acc, qoi.processIndicatorOrder), 0),
    [processIndicatorsData],
  );
  const isDirty = React.useMemo(
    () =>
      JSON.stringify(sortedProcessIndicatorOrder.map(qoi => qoi.name)) !==
      JSON.stringify(processIndicatorsData.map(qoi => qoi.name)),
    [processIndicatorsData, sortedProcessIndicatorOrder],
  );
  const valid = isDirty && processIndicatorsData.every(qoi => qoi.id !== "");

  const handleSubmit = React.useCallback(async () => {
    await updateProcessIndicators({
      routeParams: { projectId },
      body: processIndicatorsData.map(({ id }) => ({ id })),
    });

    return true;
  }, [projectId, processIndicatorsData, updateProcessIndicators]);

  const handleShowNewQoi = React.useCallback(() => {
    setProcessIndicators(previousQois => [
      ...previousQois,
      { id: "", processIndicatorOrder: maxProcessIndicatorsOrder + 1, name: "" },
    ]);
  }, [maxProcessIndicatorsOrder]);

  const disableAddButton = processIndicatorsData.length >= MAX_PROCESS_INDICATOR_COUNT;

  return (
    <ModalForm
      onSubmit={valid ? handleSubmit : false}
      onRequestClose={closeModal}
      okButtonName={t("modalForm.button.name.save")}
    >
      {processIndicatorsData.length > 0 ? (
        <Table datas={processIndicatorsData} metadata={metadata} computeRowKey={computeRowKey} />
      ) : (
        <>{t("project.settings.processIndicator.no.data")}</>
      )}

      {canUserEdit && (
        <>
          <StyledButton
            variant="link"
            onClick={handleShowNewQoi}
            leftIcon={<AddCircleLine />}
            disabled={disableAddButton}
          >
            {t("project.settings.processIndicator.add.new")}
          </StyledButton>
        </>
      )}
    </ModalForm>
  );
};

export default ManageProcessIndicatorModalForm;
